import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Upload, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

async function uploadFile(file) {
    const fd = new FormData(); fd.append('file', file); fd.append('kind', 'image');
    const { data } = await api.post('/upload', fd);
    return data.url;
}

export const AiListingWizard = ({ open, onOpenChange, onFilled }) => {
    const [step, setStep] = useState('upload'); // upload | preview | done
    const [imageUrl, setImageUrl] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef();

    const reset = () => { setStep('upload'); setImageUrl(''); setResult(null); };

    const onSelect = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setBusy(true);
        try {
            const url = await uploadFile(f);
            setImageUrl(url);
            setStep('preview');
            const abs = url.startsWith('http') ? url : `${(process.env.REACT_APP_BACKEND_URL || '')}${url}`;
            const { data } = await api.post('/ai/listing-from-image-v2', { image_url: abs });
            setResult(data);
            if (data._fallback || data._error) toast.warning('IA en modo simulación — revisa/edita los campos.');
            else toast.success('Anuncio prellenado con IA.');
        } catch (e) { toast.error('No se pudo analizar la imagen'); }
        finally { setBusy(false); }
    };

    const useResult = () => {
        if (!result) return;
        onFilled({
            title: result.title,
            description: result.description,
            category_slug: result.category_slug,
            condition: result.condition,
            tags: (result.tags || []).slice(0, 8),
            suggested_price: result.suggested_price_mxn,
            image_url: imageUrl,
            attributes: result.attributes || {},
            brand: result.brand,
        });
        onOpenChange(false);
        reset();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-red-600" />Publicar con IA — 1 foto → anuncio completo</DialogTitle>
                    <DialogDescription>Sube una foto nítida del producto. La IA sugerirá título, descripción, categoría, precio y tags. Podrás editar todo antes de publicar.</DialogDescription>
                </DialogHeader>
                {step === 'upload' && (
                    <div className="border-2 border-dashed rounded-lg p-8 text-center">
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onSelect} data-testid="ai-listing-file" />
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                        <div className="mt-2 text-sm text-muted-foreground">Foto del producto</div>
                        <Button onClick={() => fileRef.current?.click()} className="mt-3 bg-red-600 hover:bg-red-700" disabled={busy} data-testid="ai-listing-choose">{busy ? 'Analizando…' : 'Elegir imagen'}</Button>
                    </div>
                )}
                {step === 'preview' && result && (
                    <div className="space-y-3">
                        {imageUrl && <img src={imageUrl.startsWith('http') ? imageUrl : `${(process.env.REACT_APP_BACKEND_URL || '')}${imageUrl}`} alt="" className="w-full h-40 object-cover rounded-lg" />}
                        <div className="grid grid-cols-1 gap-2 text-sm">
                            <div><strong>Título:</strong> {result.title}</div>
                            <div><strong>Descripción:</strong> <span className="text-muted-foreground">{result.description}</span></div>
                            <div><strong>Categoría:</strong> {result.category_slug} • <strong>Condición:</strong> {result.condition}</div>
                            <div><strong>Precio sugerido:</strong> ${result.suggested_price_mxn} MXN</div>
                            <div><strong>Tags:</strong> {(result.tags || []).join(', ')}</div>
                            {result.brand && <div><strong>Marca:</strong> {result.brand}</div>}
                        </div>
                        {(result._fallback || result._error) && (
                            <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">MODO SIMULACIÓN: configura OpenRouter en Superadmin para IA real.</div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    {step === 'preview' && <><Button variant="outline" onClick={reset}>Reintentar</Button><Button onClick={useResult} className="bg-red-600 hover:bg-red-700" data-testid="ai-listing-use"><Wand2 className="h-4 w-4 mr-1" />Usar y editar</Button></>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
